import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FileUploader } from '@/components/common'
import type { CustomFieldDefinitionView } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

export interface CustomFieldInputProps {
  field: CustomFieldDefinitionView
  value: unknown
  onChange: (code: string, value: unknown) => void
  disabled?: boolean
}

/**
 * Renders one admin-defined field from its definition.
 *
 * <p>This is the renderer half of the custom-fields engine. It lives here rather than on the
 * server on purpose: every input component already exists in this app, and a second rendering
 * path in Java would be two implementations of the same six field types drifting apart. The
 * server owns the schema and the validation; the browser owns the pixels.
 *
 * <p>The constraints are mirrored onto the inputs — `required`, `min`, `max`, `minLength`,
 * `pattern` — so the browser catches what it can before a round trip. That is a convenience,
 * not the check: the server validates the same rules again, because anything sent by a
 * client is a claim rather than a fact.
 */
/**
 * Renders a stored value as input text.
 *
 * <p>Values come out of a jsonb column, so the declared type is a claim rather than a
 * guarantee: a row written before a field changed type can hold a number where a string is
 * expected, or an object where neither is. Narrowing explicitly keeps "[object Object]" out
 * of the box somebody is about to save.
 */
function asText(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value) ?? ''
}

export function CustomFieldInput({ field, value, onChange, disabled }: CustomFieldInputProps) {
  const id = `cf-${field.code}`
  // Values arrive from a jsonb column, so the type is a claim rather than a guarantee: a row
  // written before a field changed type can hold an object where a string is expected, and
  // String(...) on one renders "[object Object]" into the input.
  const text = asText(value)

  const describedBy = field.helpText ? `${id}-help` : undefined
  const help = field.helpText && (
    <p id={`${id}-help`} className="text-xs text-[var(--color-muted)]">
      {field.helpText}
    </p>
  )

  if (field.fieldType === 'DROPDOWN') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id} required={field.required}>{field.label}</Label>
        <Select
          id={id}
          required={field.required}
          disabled={disabled}
          aria-describedby={describedBy}
          value={text}
          onChange={(event) => onChange(field.code, event.target.value || null)}
          className="w-full"
        >
          <option value="">Choose…</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>
        {help}
      </div>
    )
  }

  if (field.fieldType === 'FILE') {
    return (
      <div className="space-y-1.5">
        <Label required={field.required}>{field.label}</Label>
        <FileUploader
          ownerType="custom_field"
          disabled={disabled}
          onUploaded={(file) => onChange(field.code, file.id)}
        />
        {text && <p className="text-xs text-[var(--color-muted)]">Attached: {text}</p>}
        {help}
      </div>
    )
  }

  // A DATE field gets the app's picker rather than the browser's: the native control
  // formats itself from the browser's locale, not the company's, and draws a calendar
  // button of its own that no styling can reach.
  if (field.fieldType === 'DATE') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id} required={field.required}>{field.label}</Label>
        <DatePicker
          id={id}
          required={field.required}
          disabled={disabled}
          aria-describedby={describedBy}
          value={text}
          min={field.minDate ?? undefined}
          max={field.maxDate ?? undefined}
          onChange={(event) => onChange(field.code, event.target.value || null)}
        />
        {help}
      </div>
    )
  }

  const type = field.fieldType === 'NUMBER' ? 'number' : 'text'

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required={field.required}>{field.label}</Label>
      <Input
        id={id}
        type={type}
        required={field.required}
        disabled={disabled}
        aria-describedby={describedBy}
        value={text}
        // Mirrored from the definition so the browser catches what it can. The server
        // checks all of it again regardless.
        min={field.fieldType === 'NUMBER' ? field.minNumber ?? undefined : undefined}
        max={field.fieldType === 'NUMBER' ? field.maxNumber ?? undefined : undefined}
        minLength={field.fieldType === 'TEXT' ? field.minLength ?? undefined : undefined}
        maxLength={field.fieldType === 'TEXT' ? field.maxLength ?? undefined : undefined}
        pattern={field.fieldType === 'TEXT' ? field.regex ?? undefined : undefined}
        placeholder={field.fieldType === 'LOOKUP' ? `${field.lookupSource ?? 'record'} id` : undefined}
        onChange={(event) => onChange(field.code, event.target.value || null)}
      />
      {help}
    </div>
  )
}

/** Renders a whole schema, in the order the administrator arranged it. */
export function CustomFieldForm({
  fields,
  values,
  onChange,
  disabled,
}: {
  fields: CustomFieldDefinitionView[]
  values: Record<string, unknown>
  onChange: (code: string, value: unknown) => void
  disabled?: boolean
}) {
  if (fields.length === 0) {
    return null
  }
  return (
    <div className="space-y-4">
      {fields.map((field) => (
        <CustomFieldInput
          key={field.id}
          field={field}
          value={values[field.code]}
          onChange={onChange}
          disabled={disabled}
        />
      ))}
    </div>
  )
}
